// src/screens/AdminCompOverviewScreen.js
// ============================================================================
// ADMIN — CREATOR COMP OVERVIEW
//
// Monthly + yearly rollup of the comp engine's books, from adminGetCompOverview:
//   - revenue by source (live vs Stripe-test; comp-funding subtotal)
//   - pool / allocation history (creatorCompRuns, incl. test-mode previews)
//   - per-axis signal totals
//   - audience totals: signed-in (earns comp) vs anonymous (conversion target)
//   - top creators (paid from compLedger; planned from previews/projection)
//   - Super Crits credited to creators (direct lane, from earningsLedger) —
//     paid outside the monthly run, so shown beside comp paid and in the P&L
//   - Rumbles credited to recipients (evergreen 3.3, from earningsLedger) —
//     same posture, on their own line beside the Super Crit one
//   - platform P&L: comp counted ledger-first (paid, plus the unpaid remainder
//     only while comp is live), each comp row labeled by its basis
//   - per-recipient membership credits (by tier) and Super Crit / Rumble
//     credits, from the same earningsLedger reads
//   - membership memo lines under the net (memo only)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, SafeAreaView, RefreshControl, TextInput, Switch,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { membershipDetailText, directDetailText, tierRows, membershipMemoLines, directMemoLines, memoErrorLines, creditedCountText } from '../utils/compOverviewCredits';

const functions = getFunctions();

const COLORS = {
  BG: '#0a0a0a',
  CARD: '#111',
  GOLD: '#FCD000',
  TEXT: '#fff',
  DIM: '#888',
  BORDER: '#222',
  SUCCESS: '#4CAF50',
  DANGER: '#ff4444',
  CYAN: '#22d3ee',
};

// Sign-aware: net profit and projector profit go negative — "-$123.45",
// not "$-123.45".
const fmt$ = (cents) => {
  const v = (cents || 0) / 100;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const fmtNum = (n) => {
  const v = Math.round((Number(n) || 0) * 10) / 10;
  return Number.isInteger(v) ? v.toLocaleString('en-US') : v.toFixed(1);
};
// Direct-lane Super Crits (directTipCents): net credited straight to creators'
// earnings when the charge succeeds (earningsLedger rows pending|available,
// live only). The monthly run never pays these, so they are NOT in comp paid
// or the pool; the server subtracts them once in the platform net. Read from
// the platform block first, then the month/year object itself. An older
// server omits the field -> null -> the line renders nothing (a $0.00 line
// would claim a figure that server never computed).
const directTipsOf = (...sources) => {
  for (const s of sources) {
    const v = s?.directTipCents;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
};
// Rumbles credited to recipients (evergreen 3.3, revenue.rumbleCreditedCents):
// net credited straight to the streamers, squad members and event players who
// received them. Same posture as directTipsOf: in no run field, subtracted
// once in the platform net, on its own line so the Super Crit line keeps
// meaning Super Crits only. Platform block first, then the month's revenue
// (or the year totals). An older server omits it -> null -> no line.
const rumbleCreditedOf = (...sources) => {
  for (const s of sources) {
    const v = s?.rumbleCreditedCents;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
};
// Membership credits (3.5a, revenue.membershipCreditedCents): net credited
// straight to creators from channel membership payments. Same posture as
// rumbleCreditedOf: in no run field, subtracted once in the platform net, on
// its own line. An older server omits it -> null -> no line.
const membershipCreditedOf = (...sources) => {
  for (const s of sources) {
    const v = s?.membershipCreditedCents;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
};
// Display names for revenue source keys; any other key shows as stored.
// 'subscription' is the platform Plan product, never memberships.
const REVENUE_SOURCE_LABELS = {
  membership: 'Memberships',
  team_membership: 'Team memberships',
  subscription: 'Plans',
};
// Team memberships (3.5b): a team payment is split after its month closes,
// so its credits land in a later month's Membership credits line than the
// revenue row. Shown under the revenue rows whenever a team_membership row is
// present (admin-facing memo; no server field).
const TEAM_MEMBERSHIP_REVENUE_NOTE = "Team membership revenue is split among the team's members after its month closes; the credits appear in the next month's Membership credits (two months later when a month had no active member).";
const monthName = (monthKey) => {
  const [y, m] = String(monthKey || '').split('-').map(Number);
  if (!y || !m) return monthKey;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
};
const utcMonthKey = (d = new Date()) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
const isCents = (v) => typeof v === 'number' && Number.isFinite(v);

// How a month's comp rows were counted (m.comp.basis, mirrored on
// m.platform.compBasis). Ledger first: 'paid' = what compLedger says was
// paid; 'paid_plus_remaining' / 'projected' add the unpaid remainder from a
// dry run (comp live only); 'not_counted' = a plan exists but nothing was
// paid or counted (test mode / comp off). 'none', unknown, or an older server
// with no basis -> null -> no label.
const COMP_BASIS_LABELS = {
  paid: 'paid',
  paid_plus_remaining: 'paid + remaining',
  projected: 'projected',
  not_counted: 'not counted',
};
const compBasisLabel = (basis) =>
  (typeof basis === 'string' && Object.prototype.hasOwnProperty.call(COMP_BASIS_LABELS, basis) ? COMP_BASIS_LABELS[basis] : null);
const monthCompBasis = (m) => m?.comp?.basis ?? m?.platform?.compBasis;

// Year block: the year platform sums each month's COUNTED comp, so label it by
// what the months counted. A remainder counted anywhere (comp live, month not
// fully paid) -> "paid + projected remainder"; otherwise ledger-paid only
// (a past year, or comp not live). An older server (no month carries a basis)
// -> null -> today's label.
const yearCompBasisText = (months) => {
  const bases = (months || []).map(monthCompBasis).filter((b) => typeof b === 'string');
  if (!bases.length) return null;
  return bases.some((b) => b === 'projected' || b === 'paid_plus_remaining')
    ? 'paid + projected remainder'
    : 'paid';
};

// Comp row labels in the P&L block. newShape = the server sent a comp basis
// (general pool and run lane are separate figures); an older server keeps
// today's single "pool" label.
const compRowLabels = (newShape, basisText) => {
  const suffix = basisText ? ` (${basisText})` : '';
  return {
    general: newShape ? `− Creator comp, general pool${suffix}` : '− Creator comp pool',
    lane: `− Creator comp, floods + Super Crits (run lane)${newShape ? suffix : ''}`,
  };
};
// The run-lane row (floods + run-lane Super Crits) shows only when the server
// subtracted something for it.
const showLaneRow = (p) => isCents(p?.laneCents) && p.laneCents > 0;

// Memo lines under a month's net: planned comp the net did NOT count, and on
// a projected month what the unverified creators would add if they verify in
// time (the run pays verified creators only).
const compMemoLines = (comp, isCurrentMonth = false) => {
  if (!comp) return [];
  const out = [];
  if (isCents(comp.notCountedCents) && comp.notCountedCents > 0) {
    out.push(`Not counted: ${fmt$(comp.notCountedCents)} planned (test mode / comp off)`);
  }
  if (comp.basis === 'projected' && isCents(comp.ineligibleRetainedCents) && comp.ineligibleRetainedCents > 0) {
    out.push(`${fmt$(comp.ineligibleRetainedCents)} more if unverified creators verify before ${isCurrentMonth ? 'the 1st' : 'the run'}`);
  }
  return out;
};

// Server read failures the net above them is missing (adminGetCompOverview
// reports them rather than silently counting 0). Year block: pass
// projectionErrors (monthKeys); a month card: projectionError + monthKey.
const hasError = (v) => v != null && v !== false;
const incompleteWarnings = ({ expensesError, projectionErrors, projectionError, monthKey } = {}) => {
  const out = [];
  if (hasError(expensesError)) out.push('Incomplete: expenses could not be read');
  const failed = hasError(projectionError) ? [monthKey] : (Array.isArray(projectionErrors) ? projectionErrors : []);
  if (failed.length) out.push(`Incomplete: projection failed for ${failed.map(monthName).join(', ')}`);
  return out;
};

// Month status chip. Completed shows what compLedger says was paid (the run
// doc's actuallyDistributedCents on an older server); in-progress and failed
// runs get their own chips instead of falling through to NO RUN. The current
// month's fresh projection wins over a stale preview doc.
const chipFor = (m) => {
  const status = m?.run?.status;
  const paid = isCents(m?.comp?.paidCents) ? m.comp.paidCents : null;
  if (status === 'completed') return { text: `PAID ${fmt$(paid != null ? paid : m.run.actuallyDistributedCents)}`, kind: 'paid' };
  if (status === 'in_progress') return { text: paid != null ? `IN PROGRESS · ${fmt$(paid)} paid so far` : 'IN PROGRESS', kind: 'preview' };
  if (status === 'failed') return { text: 'FAILED · re-run if retries are used up', kind: 'failed' };
  if (m?.projection) return { text: `PROJECTED pool ${fmt$(m.projection.poolCents)}`, kind: 'preview' };
  if (status === 'preview') return { text: `PREVIEW ${fmt$(m.run.plannedCents)}`, kind: 'preview' };
  return { text: 'NO RUN', kind: 'none' };
};

// Per-axis display units (mirrors the Wallet metrics section; floods = cents).
const AXIS_META = [
  { key: 'placement', label: 'Placement', unit: 'pts' },
  { key: 'earnedCrits', label: 'Crits Won', unit: '' },
  { key: 'participation', label: 'Participation', unit: 'pts' },
  { key: 'streaming', label: 'Streamed', unit: 'min' },
  { key: 'audienceDraw', label: 'Audience', unit: 'viewer-min' },
  { key: 'floods', label: 'Boosts', unit: '$', money: true },
  { key: 'referrals', label: 'Referrals', unit: '' },
  { key: 'hosting', label: 'Hosted', unit: '' },
  { key: 'verifiedSellouts', label: 'Sellouts', unit: '' },
  { key: 'verifiedFillRate', label: 'Fill Rate', unit: '' },
];

export default function AdminCompOverviewScreen({ navigation }) {
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Engine config editor (display units; parsed to the callable on save).
  const [cfg, setCfg] = useState(null);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfgMsg, setCfgMsg] = useState(null);

  // What-if projector.
  const [showProjector, setShowProjector] = useState(false);
  const [projRev, setProjRev] = useState('5000');
  const [projCreators, setProjCreators] = useState('50');
  const [proj, setProj] = useState(null);
  const [projLoading, setProjLoading] = useState(false);

  const load = useCallback(async (y) => {
    try {
      setError(null);
      const fn = httpsCallable(functions, 'adminGetCompOverview');
      const result = await fn({ year: y });
      setData(result.data || null);
    } catch (err) {
      console.error('[AdminCompOverview] load failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      setLoading(true);
      load(year);
    } else if (!authLoading && !isAdmin) {
      setLoading(false);
    }
  }, [authLoading, isAdmin, year, load]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await httpsCallable(functions, 'adminGetCreatorCompConfig')({});
      const c = res.data || {};
      setCfg({
        enabled: !!c.enabled,
        testMode: c.testMode !== false,
        allocationMode: c.allocationMode === 'flat' ? 'flat' : 'auto',
        monthlyAllocationPercent: String(c.monthlyAllocationPercent ?? 40),
        stripeFeePercent: String(c.stripeFeePercent ?? 3),
        costFloor: ((c.platformCostFloorCents ?? 0) / 100).toFixed(2),
        targetMarginPercent: String(c.targetMarginPercent ?? 10),
        surplusSharePercent: String(c.surplusSharePercent ?? 65),
        maxAllocationPercent: String(c.maxAllocationPercent ?? 65),
        minPayout: ((c.minPayoutCents ?? 0) / 100).toFixed(2),
        concentration: String(c.concentration ?? 1.8),
        participationPaidWeight: String(c.participationPaidWeight ?? 0.15),
        floodCompPercent: String(c.floodCompPercent ?? 0.9),
        weights: Object.fromEntries(AXIS_META.map((a) => [a.key, String(c.weights?.[a.key] ?? 0)])),
      });
      setCfgLoaded(true);
    } catch (e) {
      console.error('[AdminCompOverview] config load failed:', e);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAdmin) loadConfig();
  }, [authLoading, isAdmin, loadConfig]);

  const onRefresh = () => { setRefreshing(true); load(year); loadConfig(); };

  if (authLoading || (loading && !data)) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>Admin access required</Text>
          <TouchableOpacity style={styles.backBtn} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
            <Text style={styles.backBtnText}>← Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const yt = data?.yearTotals;
  const ytDirect = directTipsOf(yt?.platform, yt);
  // Year Rumble total: the year block's own figure, else the sum of the
  // months' revenue.rumbleCreditedCents; null when no month carries it.
  const ytRumble = (() => {
    const v = rumbleCreditedOf(yt?.platform, yt);
    if (v != null) return v;
    const per = (data?.months || []).map((m) => rumbleCreditedOf(m?.platform, m?.revenue)).filter((x) => x != null);
    return per.length ? per.reduce((s, x) => s + x, 0) : null;
  })();
  // Year membership credits: same fallbacks as the Rumble total.
  const ytMembership = (() => {
    const v = membershipCreditedOf(yt?.platform, yt);
    if (v != null) return v;
    const per = (data?.months || []).map((m) => membershipCreditedOf(m?.platform, m?.revenue)).filter((x) => x != null);
    return per.length ? per.reduce((s, x) => s + x, 0) : null;
  })();
  // Year comp label: null on an older server (today's label).
  const ytBasisText = yearCompBasisText(data?.months);
  const nowMonthKey = utcMonthKey();
  // Months come oldest-first from the backend; show newest first.
  const months = [...(data?.months || [])].reverse();

  // Build the callable payload from the editor state (display units → API).
  const cfgPayload = (c) => ({
    enabled: c.enabled,
    testMode: c.testMode,
    allocationMode: c.allocationMode,
    monthlyAllocationPercent: Number(c.monthlyAllocationPercent),
    stripeFeePercent: Number(c.stripeFeePercent),
    platformCostFloorCents: Math.round(Number(c.costFloor) * 100),
    targetMarginPercent: Number(c.targetMarginPercent),
    surplusSharePercent: Number(c.surplusSharePercent),
    maxAllocationPercent: Number(c.maxAllocationPercent),
    minPayoutCents: Math.round(Number(c.minPayout) * 100),
    concentration: Number(c.concentration),
    participationPaidWeight: Number(c.participationPaidWeight),
    floodCompPercent: Number(c.floodCompPercent),
    weights: Object.fromEntries(AXIS_META.map((a) => [a.key, Number(c.weights[a.key])])),
  });
  const setCfgField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setWeight = (k, v) => setCfg((c) => ({ ...c, weights: { ...c.weights, [k]: v } }));

  const saveConfig = async () => {
    if (!cfg) return;
    setSavingCfg(true); setCfgMsg(null);
    try {
      await httpsCallable(functions, 'adminSetCreatorCompConfig')(cfgPayload(cfg));
      setCfgMsg('Saved.');
      load(year);       // refresh mode badge + projections with new knobs
    } catch (e) {
      setCfgMsg(e.message || 'Save failed.');
    } finally {
      setSavingCfg(false);
    }
  };

  const runProjection = async () => {
    setProjLoading(true);
    try {
      const res = await httpsCallable(functions, 'adminComputeCompScenario')({
        revenueCents: Math.round(Number(projRev) * 100),
        creators: Math.round(Number(projCreators)),
        config: cfg ? cfgPayload(cfg) : undefined,   // reflect unsaved edits
      });
      setProj(res.data || null);
    } catch (e) {
      console.error('[AdminCompOverview] projection failed:', e);
    } finally {
      setProjLoading(false);
    }
  };

  // Reusable numeric field + weight field for the settings editor.
  const numField = (label, key) => (
    <View style={styles.fieldRow} key={key}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={styles.input}
        value={cfg[key]}
        onChangeText={(v) => setCfgField(key, v)}
        keyboardType="numeric"
        placeholderTextColor="#555"
      />
    </View>
  );
  const weightField = (a) => (
    <View style={styles.fieldRow} key={a.key}>
      <Text style={styles.fieldLabel}>{a.label}</Text>
      <TextInput
        style={styles.input}
        value={cfg.weights[a.key]}
        onChangeText={(v) => setWeight(a.key, v)}
        keyboardType="numeric"
      />
    </View>
  );

  // Platform P&L block (after paying creators + real costs). directCents is
  // the direct-lane Super Crit total (directTipsOf) and rumbleCents the
  // Rumble total (rumbleCreditedOf) — each null on an older server, which
  // renders no line. Membership credits come from the platform object itself
  // (p.membershipCreditedCents, 3.5a), so the signature and the calls stay
  // unchanged; missing on an older server -> no line. opts: newShape + basisText label the comp rows
  // (compRowLabels), memos are dim lines under the net, warnings are the red
  // "Incomplete" lines. Display only: every figure (net included) is the
  // server's; nothing is recomputed here.
  const renderPlatform = (p, directCents = null, rumbleCents = null, opts = {}) => {
    if (!p) return null;
    const net = p.netProfitCents || 0;
    const { newShape = false, basisText = null, memos = [], warnings = [] } = opts;
    const compLabels = compRowLabels(newShape, basisText);
    const membershipCents = membershipCreditedOf(p);
    return (
      <>
        <Text style={styles.subTitle}>Platform (after comp + costs)</Text>
        <View style={styles.plRow}><Text style={styles.plLabel}>Revenue (live)</Text><Text style={styles.plVal}>{fmt$(p.revenueCents)}</Text></View>
        <View style={styles.plRow}><Text style={styles.plLabel}>− Stripe fees (est.)</Text><Text style={styles.plNeg}>−{fmt$(p.stripeFeesCents)}</Text></View>
        <View style={styles.plRow}><Text style={styles.plLabel}>{compLabels.general}</Text><Text style={styles.plNeg}>−{fmt$(p.poolCents)}</Text></View>
        {showLaneRow(p) && (
          <View style={styles.plRow}><Text style={styles.plLabel}>{compLabels.lane}</Text><Text style={styles.plNeg}>−{fmt$(p.laneCents)}</Text></View>
        )}
        {directCents != null && (
          <View style={styles.plRow}><Text style={styles.plLabel}>− Super Crits credited to creators (direct)</Text><Text style={styles.plNeg}>−{fmt$(directCents)}</Text></View>
        )}
        {rumbleCents != null && (
          <View style={styles.plRow}><Text style={styles.plLabel}>− Rumbles credited to recipients</Text><Text style={styles.plNeg}>−{fmt$(rumbleCents)}</Text></View>
        )}
        {membershipCents != null && (
          <View style={styles.plRow}><Text style={styles.plLabel}>− Membership credits</Text><Text style={styles.plNeg}>−{fmt$(membershipCents)}</Text></View>
        )}
        <View style={styles.plRow}><Text style={styles.plLabel}>− Operating expenses</Text><Text style={styles.plNeg}>−{fmt$(p.opExpensesCents)}</Text></View>
        <View style={[styles.plRow, styles.plNetRow]}>
          <Text style={styles.plNetLabel}>Net profit</Text>
          <Text style={[styles.plNet, { color: net >= 0 ? COLORS.SUCCESS : COLORS.DANGER }]}>{fmt$(net)}</Text>
        </View>
        {warnings.map((w) => (
          <Text key={w} style={[styles.dimLine, { color: COLORS.DANGER }]}>{w}</Text>
        ))}
        {memos.map((t) => (
          <Text key={t} style={styles.dimLine}>{t}</Text>
        ))}
        <Text style={styles.dimLine}>Excludes merch COGS — reconciles with the Financial Overview.</Text>
      </>
    );
  };

  const renderRevenueRows = (bySource) => {
    const entries = Object.entries(bySource || {}).sort((a, b) => (b[1].liveCents || 0) - (a[1].liveCents || 0));
    if (!entries.length) return <Text style={styles.dimLine}>No revenue recorded.</Text>;
    const rows = entries.map(([src, b]) => (
      <View key={src} style={styles.revRow}>
        <Text style={styles.revSource}>{REVENUE_SOURCE_LABELS[src] || src}</Text>
        <Text style={styles.revLive}>{fmt$(b.liveCents)}</Text>
        {(b.testCents || 0) !== 0 && <Text style={styles.revTest}>test {fmt$(b.testCents)}</Text>}
      </View>
    ));
    if (!bySource.team_membership) return rows;
    return [
      ...rows,
      <Text key=":team_membership_note" style={styles.dimLine}>{TEAM_MEMBERSHIP_REVENUE_NOTE}</Text>,
    ];
  };

  const renderCreators = (list, emptyLabel) => {
    if (!list?.length) return <Text style={styles.dimLine}>{emptyLabel}</Text>;
    return list.map((c, i) => (
      <View key={c.uid} style={styles.creatorRow}>
        <Text style={styles.creatorRank}>{i + 1}</Text>
        <Text style={styles.creatorName} numberOfLines={1}>{c.name}</Text>
        <Text style={styles.creatorAmt}>{fmt$(c.amountCents)}</Text>
      </View>
    ));
  };

  // Credit lists (membership credits; Super Crits and Rumbles): the creator
  // row plus a dim detail line under it when detailOf(c) has one. With no
  // emptyLabel an empty list renders nothing (the month cards).
  const renderCreditList = (list, emptyLabel, detailOf) => {
    if (!list?.length) return emptyLabel === undefined ? null : <Text style={styles.dimLine}>{emptyLabel}</Text>;
    return list.map((c, i) => {
      const detail = detailOf(c);
      return (
        <View key={c.uid}>
          <View style={styles.creatorRow}>
            <Text style={styles.creatorRank}>{i + 1}</Text>
            <Text style={styles.creatorName} numberOfLines={1}>{c.name}</Text>
            <Text style={styles.creatorAmt}>{fmt$(c.amountCents)}</Text>
          </View>
          {detail != null && <Text style={[styles.dimLine, { marginLeft: 22 }]}>{detail}</Text>}
        </View>
      );
    });
  };

  // "Membership credits by tier": label, amount, payment count.
  const renderTierRows = (mem) => tierRows(mem).map((t) => (
    <View key={t.key} style={styles.revRow}>
      <Text style={styles.revSource}>{t.label}</Text>
      <Text style={styles.revLive}>{fmt$(t.cents)}</Text>
      <Text style={styles.revTest}>{t.countText}</Text>
    </View>
  ));

  const renderAxisGrid = (axisTotals) => (
    <View style={styles.axisGrid}>
      {AXIS_META.map((a) => {
        const raw = axisTotals?.[a.key] || 0;
        const val = a.money ? fmt$(raw) : `${fmtNum(raw)}${a.unit ? ` ${a.unit}` : ''}`;
        return (
          <View key={a.key} style={styles.axisChip}>
            <Text style={styles.axisChipValue}>{val}</Text>
            <Text style={styles.axisChipLabel}>{a.label}</Text>
          </View>
        );
      })}
    </View>
  );

  const renderAudience = (aud) => (
    <View style={styles.audRow}>
      <View style={styles.audCell}>
        <Text style={[styles.audValue, { color: COLORS.SUCCESS }]}>{fmtNum(aud?.viewers)}</Text>
        <Text style={styles.audLabel}>signed-in viewers</Text>
      </View>
      <View style={styles.audCell}>
        <Text style={[styles.audValue, { color: COLORS.SUCCESS }]}>{fmtNum((aud?.watchMinutes || 0) / 60)}</Text>
        <Text style={styles.audLabel}>hrs watched</Text>
      </View>
      <View style={styles.audCell}>
        <Text style={[styles.audValue, { color: COLORS.CYAN }]}>{fmtNum(aud?.anonViewers)}</Text>
        <Text style={styles.audLabel}>anonymous</Text>
      </View>
      <View style={styles.audCell}>
        <Text style={[styles.audValue, { color: COLORS.CYAN }]}>{fmtNum((aud?.anonWatchMinutes || 0) / 60)}</Text>
        <Text style={styles.audLabel}>anon hrs</Text>
      </View>
    </View>
  );

  const CHIP_STYLES = { paid: styles.chipPaid, preview: styles.chipPreview, failed: styles.chipFailed, none: styles.chipNone };
  const statusChip = (m) => {
    const c = chipFor(m);
    return { text: c.text, style: CHIP_STYLES[c.kind] };
  };

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.GOLD} />}
      >
        <View style={styles.titleRow}>
          <Text style={styles.title}>Creator Comp Overview</Text>
          <View style={[styles.modeBadge, data?.compTestMode ? styles.modeBadgeTest : styles.modeBadgeLive]}>
            <Text style={styles.modeBadgeText}>
              {data?.compEnabled ? (data?.compTestMode ? '🧪 TEST' : '🔴 LIVE') : '⏸ OFF'}
            </Text>
          </View>
        </View>

        {/* Year selector */}
        <View style={styles.yearRow}>
          <TouchableOpacity style={styles.yearBtn} onPress={() => setYear(year - 1)}>
            <Ionicons name="chevron-back" size={20} color={COLORS.GOLD} />
          </TouchableOpacity>
          <Text style={styles.yearText}>{data?.year ?? year}</Text>
          <TouchableOpacity
            style={[styles.yearBtn, year >= currentYear && styles.yearBtnDisabled]}
            onPress={() => year < currentYear && setYear(year + 1)}
            disabled={year >= currentYear}
          >
            <Ionicons name="chevron-forward" size={20} color={year >= currentYear ? '#444' : COLORS.GOLD} />
          </TouchableOpacity>
        </View>

        {error && <Text style={styles.errorText}>{error}</Text>}
        {loading && <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 16 }} />}

        {/* ---- ENGINE SETTINGS (editable control panel) ---- */}
        {cfgLoaded && cfg && (
          <View style={styles.card}>
            <TouchableOpacity style={styles.panelHeader} onPress={() => setShowSettings((s) => !s)}>
              <Text style={styles.cardTitle}>⚙ Engine Settings</Text>
              <Ionicons name={showSettings ? 'chevron-up' : 'chevron-down'} size={18} color={COLORS.GOLD} />
            </TouchableOpacity>
            {showSettings && (
              <View style={{ marginTop: 10 }}>
                <View style={styles.toggleRow}>
                  <Text style={styles.fieldLabel}>Enabled</Text>
                  <Switch value={cfg.enabled} onValueChange={(v) => setCfgField('enabled', v)} />
                  <Text style={[styles.fieldLabel, { marginLeft: 24 }]}>Test mode</Text>
                  <Switch value={cfg.testMode} onValueChange={(v) => setCfgField('testMode', v)} />
                </View>
                {!cfg.testMode && (
                  <Text style={[styles.dimLine, { color: COLORS.DANGER }]}>
                    ⚠ Live mode pays real money on the next monthly run. Only flip off test after Stripe is live and the ledger is purged.
                  </Text>
                )}

                <Text style={styles.subTitle}>Allocation mode</Text>
                <View style={styles.segRow}>
                  {['auto', 'flat'].map((m) => (
                    <TouchableOpacity
                      key={m}
                      style={[styles.segBtn, cfg.allocationMode === m && styles.segBtnOn]}
                      onPress={() => setCfgField('allocationMode', m)}
                    >
                      <Text style={[styles.segTxt, cfg.allocationMode === m && styles.segTxtOn]}>{m}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {cfg.allocationMode === 'auto' ? (
                  <>
                    {numField('Cost floor ($/mo)', 'costFloor')}
                    {numField('Stripe fee (%)', 'stripeFeePercent')}
                    {numField('Target margin (%)', 'targetMarginPercent')}
                    {numField('Surplus share (%)', 'surplusSharePercent')}
                    {numField('Max cap (% of revenue)', 'maxAllocationPercent')}
                  </>
                ) : (
                  numField('Monthly allocation (%)', 'monthlyAllocationPercent')
                )}
                {numField('Min payout ($)', 'minPayout')}

                <TouchableOpacity style={styles.advToggle} onPress={() => setShowAdvanced((a) => !a)}>
                  <Text style={styles.advToggleTxt}>{showAdvanced ? '▾' : '▸'} Advanced — weights, concentration</Text>
                </TouchableOpacity>
                {showAdvanced && (
                  <>
                    {numField('Concentration (gamma 0.5–3)', 'concentration')}
                    {numField('Participation paid weight k (0–1)', 'participationPaidWeight')}
                    {numField('Flood comp fraction (0–1)', 'floodCompPercent')}
                    <Text style={styles.subTitle}>Axis weights (relative)</Text>
                    {AXIS_META.map((a) => weightField(a))}
                  </>
                )}

                <TouchableOpacity style={[styles.saveBtn, savingCfg && { opacity: 0.6 }]} onPress={saveConfig} disabled={savingCfg}>
                  {savingCfg ? <ActivityIndicator color="#000" size="small" /> : <Text style={styles.saveBtnTxt}>Save engine settings</Text>}
                </TouchableOpacity>
                {cfgMsg && <Text style={[styles.dimLine, { textAlign: 'center', marginTop: 6 }]}>{cfgMsg}</Text>}
              </View>
            )}
          </View>
        )}

        {/* ---- WHAT-IF PROJECTOR ---- */}
        <View style={styles.card}>
          <TouchableOpacity style={styles.panelHeader} onPress={() => setShowProjector((s) => !s)}>
            <Text style={styles.cardTitle}>📈 What-if projector</Text>
            <Ionicons name={showProjector ? 'chevron-up' : 'chevron-down'} size={18} color={COLORS.GOLD} />
          </TouchableOpacity>
          {showProjector && (
            <View style={{ marginTop: 10 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Target revenue ($/mo)</Text>
                <TextInput style={styles.input} value={projRev} onChangeText={setProjRev} keyboardType="numeric" />
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Active creators</Text>
                <TextInput style={styles.input} value={projCreators} onChangeText={setProjCreators} keyboardType="numeric" />
              </View>
              <TouchableOpacity style={[styles.saveBtn, projLoading && { opacity: 0.6 }]} onPress={runProjection} disabled={projLoading}>
                {projLoading ? <ActivityIndicator color="#000" size="small" /> : <Text style={styles.saveBtnTxt}>Project</Text>}
              </TouchableOpacity>
              {proj && (
                <View style={{ marginTop: 12 }}>
                  <View style={styles.statRow}>
                    <View style={styles.statCell}><Text style={styles.statValue}>{fmt$(proj.poolCents)}</Text><Text style={styles.statLabel}>comp pool</Text></View>
                    <View style={styles.statCell}><Text style={styles.statValue}>{proj.effectivePercent}%</Text><Text style={styles.statLabel}>of revenue</Text></View>
                    <View style={styles.statCell}>
                      <Text style={[styles.statValue, { color: proj.platformProfitCents >= 0 ? COLORS.SUCCESS : COLORS.DANGER }]}>{fmt$(proj.platformProfitCents)}</Text>
                      <Text style={styles.statLabel}>platform profit</Text>
                    </View>
                  </View>
                  {proj.tiers && (
                    <>
                      <Text style={styles.subTitle}>Creator payouts / mo (illustrative)</Text>
                      <View style={styles.plRow}><Text style={styles.plLabel}>Top creator</Text><Text style={styles.plVal}>{fmt$(proj.tiers.top.amountCents)}</Text></View>
                      <View style={styles.plRow}><Text style={styles.plLabel}>Median (#{proj.tiers.median.rank})</Text><Text style={styles.plVal}>{fmt$(proj.tiers.median.amountCents)}</Text></View>
                      <View style={styles.plRow}><Text style={styles.plLabel}>Casual (#{proj.tiers.casual.rank})</Text><Text style={styles.plVal}>{fmt$(proj.tiers.casual.amountCents)}</Text></View>
                      <View style={styles.plRow}><Text style={styles.plLabel}>Average</Text><Text style={styles.plVal}>{fmt$(proj.averageCents)}</Text></View>
                    </>
                  )}
                  <Text style={styles.dimLine}>
                    Pool + profit are exact (real allocation math, at your {fmt$(proj.costFloorCents)} cost floor). Tiers assume a typical activity spread + your concentration setting.
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* ---- YEAR ROLLUP ---- */}
        {yt && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Year {data.year}</Text>
            <View style={styles.statRow}>
              <View style={styles.statCell}>
                <Text style={styles.statValue}>{fmt$(yt.totalLiveCents)}</Text>
                <Text style={styles.statLabel}>revenue (live)</Text>
              </View>
              <View style={styles.statCell}>
                <Text style={styles.statValue}>{fmt$(yt.compFundingCents)}</Text>
                <Text style={styles.statLabel}>comp-funding</Text>
              </View>
              <View style={styles.statCell}>
                <Text style={[styles.statValue, { color: COLORS.SUCCESS }]}>{fmt$(yt.paidCents)}</Text>
                <Text style={styles.statLabel}>comp paid</Text>
              </View>
              {ytDirect != null && (
                <View style={styles.statCell}>
                  <Text style={[styles.statValue, { color: COLORS.SUCCESS }]}>{fmt$(ytDirect)}</Text>
                  <Text style={[styles.statLabel, { textAlign: 'center' }]}>Super Crits credited to creators</Text>
                </View>
              )}
              {ytRumble != null && (
                <View style={styles.statCell}>
                  <Text style={[styles.statValue, { color: COLORS.SUCCESS }]}>{fmt$(ytRumble)}</Text>
                  <Text style={[styles.statLabel, { textAlign: 'center' }]}>Rumbles credited to recipients</Text>
                </View>
              )}
              {ytMembership != null && (
                <View style={styles.statCell}>
                  <Text style={[styles.statValue, { color: COLORS.SUCCESS }]}>{fmt$(ytMembership)}</Text>
                  <Text style={[styles.statLabel, { textAlign: 'center' }]}>Membership credits</Text>
                </View>
              )}
              <View style={styles.statCell}>
                <Text style={[styles.statValue, { color: COLORS.GOLD }]}>{fmt$(yt.plannedCents)}</Text>
                <Text style={styles.statLabel}>planned/preview</Text>
              </View>
            </View>
            <Text style={styles.subTitle}>Revenue by source</Text>
            {renderRevenueRows(yt.revenueBySource)}
            {(yt.totalTestCents || 0) !== 0 && (
              <Text style={styles.dimLine}>
                Stripe-test rows on the books: {fmt$(yt.totalTestCents)} — never fund comp.
              </Text>
            )}
            <Text style={styles.subTitle}>Signals ({yt.activeCreators} active creator{yt.activeCreators === 1 ? '' : 's'})</Text>
            {renderAxisGrid(yt.axisTotals)}
            <Text style={styles.subTitle}>Audience</Text>
            {renderAudience(yt.audience)}
            <Text style={styles.subTitle}>Top creators — paid</Text>
            {renderCreators(yt.topPaid, data?.compTestMode ? 'Nobody paid yet (comp in test mode).' : 'Nobody paid yet — no live comp-funding revenue.')}
            <Text style={styles.subTitle}>Top creators — planned (previews + current-month projection)</Text>
            {renderCreators(yt.topPlanned, 'No previews yet.')}
            {yt.memberships && (<>
              <Text style={styles.subTitle}>Top creators: membership credits</Text>
              {renderCreditList(yt.memberships.top, 'No membership credits yet.', membershipDetailText)}
              {yt.memberships.recipientCount > 0 && <Text style={styles.dimLine}>{creditedCountText(yt.memberships.recipientCount, 'creator')}</Text>}
              {tierRows(yt.memberships).length > 0 && (<><Text style={styles.subTitle}>Membership credits by tier</Text>{renderTierRows(yt.memberships)}</>)}
            </>)}
            {yt.directByRecipient && (<>
              <Text style={styles.subTitle}>Top recipients: Super Crits and Rumbles</Text>
              {renderCreditList(yt.directByRecipient.top, 'No Super Crits or Rumbles credited yet.', directDetailText)}
              {yt.directByRecipient.recipientCount > 0 && <Text style={styles.dimLine}>{creditedCountText(yt.directByRecipient.recipientCount, 'recipient')}</Text>}
            </>)}
            {renderPlatform(yt.platform, ytDirect, ytRumble, {
              newShape: ytBasisText != null,
              basisText: ytBasisText,
              memos: [...membershipMemoLines(yt.memberships), ...directMemoLines(yt.directByRecipient), ...memoErrorLines(yt)],
              warnings: incompleteWarnings({ expensesError: yt.expensesError, projectionErrors: yt.projectionErrors }),
            })}
          </View>
        )}

        {/* ---- MONTH CARDS (newest first) ---- */}
        {months.map((m) => {
          const chip = statusChip(m);
          return (
            <View key={m.monthKey} style={styles.card}>
              <View style={styles.monthHeader}>
                <Text style={styles.cardTitle}>{monthName(m.monthKey)} <Text style={styles.monthKeyDim}>{m.monthKey}</Text></Text>
                <View style={[styles.chip, chip.style]}><Text style={styles.chipText}>{chip.text}</Text></View>
              </View>

              <View style={styles.statRow}>
                <View style={styles.statCell}>
                  <Text style={styles.statValue}>{fmt$(m.revenue.totalLiveCents)}</Text>
                  <Text style={styles.statLabel}>revenue (live)</Text>
                </View>
                <View style={styles.statCell}>
                  <Text style={styles.statValue}>{fmt$(m.revenue.compFundingCents)}</Text>
                  <Text style={styles.statLabel}>comp-funding</Text>
                </View>
                <View style={styles.statCell}>
                  <Text style={styles.statValue}>
                    {m.projection ? fmt$(m.projection.poolCents) : (m.run ? fmt$(m.run.poolCents) : '—')}
                  </Text>
                  <Text style={styles.statLabel}>pool (allocated)</Text>
                </View>
                <View style={styles.statCell}>
                  <Text style={styles.statValue}>{m.activeCreators}</Text>
                  <Text style={styles.statLabel}>creators</Text>
                </View>
              </View>

              {m.run && (
                <Text style={styles.dimLine}>
                  Run: {m.run.status} · {m.run.allocationMode || '—'} allocation
                  {m.run.effectiveAllocationPercent != null ? ` (${m.run.effectiveAllocationPercent}% effective)` : ''}
                  {' '}· {m.run.recipientCount} recipient{m.run.recipientCount === 1 ? '' : 's'}
                </Text>
              )}

              <Text style={styles.subTitle}>Revenue by source</Text>
              {renderRevenueRows(m.revenue.bySource)}

              <Text style={styles.subTitle}>Signals</Text>
              {renderAxisGrid(m.axisTotals)}

              <Text style={styles.subTitle}>Audience ({m.audience.events} event{m.audience.events === 1 ? '' : 's'})</Text>
              {renderAudience(m.audience)}

              {(m.topPaid?.length || 0) > 0 && (
                <>
                  <Text style={styles.subTitle}>Top creators — paid</Text>
                  {renderCreators(m.topPaid)}
                </>
              )}
              {(m.topPlanned?.length || 0) > 0 && (
                <>
                  <Text style={styles.subTitle}>Top creators — {m.run?.status === 'completed' ? 'planned' : (data?.compTestMode ? 'planned (nobody paid in test mode)' : 'planned (projection)')}</Text>
                  {renderCreators(m.topPlanned)}
                </>
              )}
              {(m.memberships?.top?.length || 0) > 0 && (
                <>
                  <Text style={styles.subTitle}>Top creators: membership credits</Text>
                  {renderCreditList(m.memberships.top, undefined, membershipDetailText)}
                  {m.memberships.recipientCount > 0 && <Text style={styles.dimLine}>{creditedCountText(m.memberships.recipientCount, 'creator')}</Text>}
                </>
              )}
              {tierRows(m.memberships).length > 0 && (
                <>
                  <Text style={styles.subTitle}>Membership credits by tier</Text>
                  {renderTierRows(m.memberships)}
                </>
              )}
              {(m.directByRecipient?.top?.length || 0) > 0 && (
                <>
                  <Text style={styles.subTitle}>Top recipients: Super Crits and Rumbles</Text>
                  {renderCreditList(m.directByRecipient.top, undefined, directDetailText)}
                  {m.directByRecipient.recipientCount > 0 && <Text style={styles.dimLine}>{creditedCountText(m.directByRecipient.recipientCount, 'recipient')}</Text>}
                </>
              )}
              {renderPlatform(m.platform, directTipsOf(m.platform, m), rumbleCreditedOf(m.platform, m.revenue), {
                newShape: typeof monthCompBasis(m) === 'string',
                basisText: compBasisLabel(monthCompBasis(m)),
                memos: [...compMemoLines(m.comp, m.monthKey === nowMonthKey), ...membershipMemoLines(m.memberships), ...directMemoLines(m.directByRecipient)],
                warnings: incompleteWarnings({ expensesError: yt?.expensesError, projectionError: m.projectionError, monthKey: m.monthKey }),
              })}
            </View>
          );
        })}

        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard'))}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Ionicons name="arrow-back" size={18} color="#888" style={{ marginRight: 4 }} />
            <Text style={styles.backButtonText}>Back</Text>
          </View>
        </TouchableOpacity>
      </ScrollView>
      <NavBar navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  safeArea: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flex: 1 },
  content: { padding: 16, paddingBottom: 96, maxWidth: 900, width: '100%', alignSelf: 'center' },
  loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  errorText: { color: COLORS.DANGER, fontSize: 14, marginVertical: 8, textAlign: 'center' },
  backBtn: { marginTop: 16, padding: 10 },
  backBtnText: { color: COLORS.GOLD, fontSize: 15 },

  titleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { color: COLORS.TEXT, fontSize: 20, fontWeight: '700' },
  modeBadge: { borderRadius: 6, paddingHorizontal: 10, paddingVertical: 4, borderWidth: 1 },
  modeBadgeTest: { backgroundColor: 'rgba(252,208,0,0.1)', borderColor: 'rgba(252,208,0,0.4)' },
  modeBadgeLive: { backgroundColor: 'rgba(255,68,68,0.12)', borderColor: 'rgba(255,68,68,0.45)' },
  modeBadgeText: { color: COLORS.TEXT, fontSize: 12, fontWeight: '700' },

  yearRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  yearBtn: { padding: 8 },
  yearBtnDisabled: { opacity: 0.5 },
  yearText: { color: COLORS.GOLD, fontSize: 18, fontWeight: '700', marginHorizontal: 16 },

  card: {
    backgroundColor: COLORS.CARD, borderWidth: 1, borderColor: COLORS.BORDER,
    borderRadius: 12, padding: 16, marginBottom: 16,
  },
  cardTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '700' },
  monthHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 },
  monthKeyDim: { color: COLORS.DIM, fontSize: 12, fontWeight: '400' },
  subTitle: { color: COLORS.DIM, fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 14, marginBottom: 6 },
  dimLine: { color: COLORS.DIM, fontSize: 12, marginTop: 4 },

  chip: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  chipPaid: { backgroundColor: 'rgba(76,175,80,0.15)', borderWidth: 1, borderColor: 'rgba(76,175,80,0.5)' },
  chipPreview: { backgroundColor: 'rgba(252,208,0,0.1)', borderWidth: 1, borderColor: 'rgba(252,208,0,0.4)' },
  chipNone: { backgroundColor: '#181818', borderWidth: 1, borderColor: '#2a2a2a' },
  chipFailed: { backgroundColor: 'rgba(255,68,68,0.12)', borderWidth: 1, borderColor: 'rgba(255,68,68,0.45)' },
  chipText: { color: COLORS.TEXT, fontSize: 11, fontWeight: '700' },

  statRow: { flexDirection: 'row', marginTop: 12, flexWrap: 'wrap' },
  statCell: { flex: 1, minWidth: 120, alignItems: 'center', paddingVertical: 4 },
  statValue: { color: COLORS.TEXT, fontSize: 15, fontWeight: '700' },
  statLabel: { color: COLORS.DIM, fontSize: 11, marginTop: 2 },

  revRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 3 },
  revSource: { color: COLORS.TEXT, fontSize: 13, flex: 1 },
  revLive: { color: COLORS.SUCCESS, fontSize: 13, fontWeight: '600' },
  revTest: { color: COLORS.DIM, fontSize: 11, marginLeft: 10 },

  axisGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  axisChip: {
    backgroundColor: '#181818', borderWidth: 1, borderColor: '#2a2a2a',
    borderRadius: 8, paddingVertical: 6, paddingHorizontal: 10, minWidth: 86, alignItems: 'center',
  },
  axisChipValue: { color: COLORS.GOLD, fontSize: 13, fontWeight: '700' },
  axisChipLabel: { color: COLORS.DIM, fontSize: 10, marginTop: 2 },

  audRow: { flexDirection: 'row', flexWrap: 'wrap' },
  audCell: { flex: 1, minWidth: 100, alignItems: 'center', paddingVertical: 4 },
  audValue: { fontSize: 15, fontWeight: '700' },
  audLabel: { color: COLORS.DIM, fontSize: 11, marginTop: 2 },

  creatorRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 3 },
  creatorRank: { color: COLORS.DIM, fontSize: 12, width: 22 },
  creatorName: { color: COLORS.TEXT, fontSize: 13, flex: 1, marginRight: 8 },
  creatorAmt: { color: COLORS.GOLD, fontSize: 13, fontWeight: '600' },

  backButton: { alignSelf: 'center', padding: 12, marginTop: 4 },
  backButtonText: { color: '#888', fontSize: 14 },

  // Engine settings + projector panels
  panelHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  toggleRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4, marginBottom: 4 },
  fieldRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 5 },
  fieldLabel: { color: COLORS.TEXT, fontSize: 13, flex: 1, marginRight: 10 },
  input: {
    backgroundColor: '#181818', borderWidth: 1, borderColor: '#2e2e2e', borderRadius: 8,
    color: COLORS.TEXT, fontSize: 13, paddingHorizontal: 10, paddingVertical: 6, width: 110, textAlign: 'right',
  },
  segRow: { flexDirection: 'row', gap: 8, marginBottom: 4 },
  segBtn: { paddingHorizontal: 16, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: '#2e2e2e', backgroundColor: '#181818' },
  segBtnOn: { borderColor: COLORS.GOLD, backgroundColor: 'rgba(252,208,0,0.12)' },
  segTxt: { color: COLORS.DIM, fontSize: 12, fontWeight: '700', textTransform: 'capitalize' },
  segTxtOn: { color: COLORS.GOLD },
  advToggle: { paddingVertical: 10, marginTop: 4 },
  advToggleTxt: { color: COLORS.CYAN, fontSize: 12, fontWeight: '700' },
  saveBtn: { backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 11, alignItems: 'center', marginTop: 12 },
  saveBtnTxt: { color: '#000', fontSize: 14, fontWeight: '800' },

  // Platform P&L rows
  plRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 3 },
  plLabel: { color: COLORS.DIM, fontSize: 13, flex: 1 },
  plVal: { color: COLORS.TEXT, fontSize: 13, fontWeight: '600' },
  plNeg: { color: '#c98', fontSize: 13, fontWeight: '600' },
  plNetRow: { borderTopWidth: 1, borderTopColor: COLORS.BORDER, marginTop: 4, paddingTop: 8 },
  plNetLabel: { color: COLORS.TEXT, fontSize: 14, fontWeight: '800', flex: 1 },
  plNet: { fontSize: 16, fontWeight: '800' },
});
